import React from "react";

/**
 * Shared Recharts tooltips.
 *
 * These live at module scope and are shared rather than redefined inside each
 * page (S6478). Six byte-identical copies previously sat inside their parent
 * components, which meant Recharts saw a brand-new component type on every
 * parent render and remounted the tooltip each time.
 *
 * Both are pure by design — they read only what Recharts passes in, so they need
 * no props from the page that renders them.
 */

/**
 * Tooltip for multi-series cartesian charts (stacked bars, lines).
 *
 * Used as `<Tooltip content={<SeriesTooltip />} />`. Lists every series in the
 * hovered category with its colour swatch, then the total — the swatch carries
 * identity so the series name never has to be read from colour alone.
 */
export function SeriesTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-semibold text-slate-900 mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="flex items-center gap-2 text-slate-700">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
            style={{ backgroundColor: entry.color }}
            aria-hidden="true"
          />
          <span className="flex-1">{entry.name}</span>
          <span className="font-medium tabular-nums">{entry.value}</span>
        </p>
      ))}
      <p className="mt-1 pt-1 border-t border-slate-100 flex gap-2 text-slate-500">
        <span className="flex-1">Total</span>
        <span className="font-medium tabular-nums">{total}</span>
      </p>
    </div>
  );
}

/**
 * Tooltip for the rollout / compliance pie charts.
 * Used as `<Tooltip content={<PieValueTooltip />} />`.
 */
export function PieValueTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 shadow text-sm">
      <span className="font-semibold" style={{ color: payload[0].payload.color }}>
        {payload[0].payload.label}
      </span>
      <span className="text-slate-700 ml-2">{payload[0].value} devices</span>
    </div>
  );
}
